/**
 * Typo-tolerant player name matching for the Optimizer's Player dropdown:
 * "Rogers" keeps Aaron Rodgers in the list (Leo, Oct 28, 2026; the dropdown
 * only kept names containing the typed text). Mirrors scoreName in
 * functions/utils/playerSearch.js, which ranks the server's search results;
 * keep the two in step.
 */

export const normalizeName = (s) => String(s || '')
    .normalize('NFD').replace(/[\u0300-\u036f]/g, '')
    .toLowerCase().replace(/['\u2019.]/g, '').replace(/[^a-z0-9]+/g, ' ').trim();

const editDistance = (a, b) => {
    let prev = Array.from({ length: b.length + 1 }, (_, j) => j);
    for (let i = 1; i <= a.length; i++) {
        const cur = [i];
        for (let j = 1; j <= b.length; j++) {
            cur[j] = Math.min(prev[j] + 1, cur[j - 1] + 1, prev[j - 1] + (a[i - 1] === b[j - 1] ? 0 : 1));
        }
        prev = cur;
    }
    return prev[b.length];
};

const typoLimit = (len) => (len < 4 ? 0 : len <= 5 ? 1 : 2);

const wordScore = (q, w) => {
    if (w === q) return 100;
    if (w.startsWith(q)) return 90;
    const lim = typoLimit(q.length);
    if (!lim) return 0;
    if (Math.abs(w.length - q.length) <= lim) {
        const d = editDistance(q, w);
        if (d <= lim) return 70 - 5 * d;
    }
    if (w.length > q.length && editDistance(q, w.slice(0, q.length)) <= 1) return 55;
    return 0;
};

/** 0-100: 100 exact, 95 prefix, 85 substring, else every typed word matches a name word (prefix 80, typo 50-65). */
export const scoreName = (query, name) => {
    const q = normalizeName(query);
    const n = normalizeName(name);
    if (!q || !n) return 0;
    if (n === q) return 100;
    if (n.startsWith(q)) return 95;
    if (n.includes(q)) return 85;
    const words = n.split(' ');
    let worst = 100;
    for (const qw of q.split(' ')) {
        let best = 0;
        for (const w of words) best = Math.max(best, wordScore(qw, w));
        if (!best) return 0;
        worst = Math.min(worst, best);
    }
    return worst >= 90 ? 80 : worst - 5;
};

/**
 * AutocompleteDropdown matchOptions: the names that match `typed`, in list
 * order. A typed name's list comes from the server already ranked (pro before
 * college, which the names alone can't tell); a team or game roster is A-Z.
 */
export const matchPlayerNames = (typed, names) => names.filter((name) => scoreName(typed, name) > 0);
